import React from 'react';
import './SectionLayout.css';

const resolveSource = (sources, path) => path.split('.').reduce((value, segment) => value && typeof value === 'object' ? value[segment] : undefined, sources);

// Text settings can carry versions in other languages; the visitor's
// language wins over the store's main-language text when it exists.
const localizeSection = (section, lang) => {
  const versions = section.settings?.translations;
  if (!versions || typeof versions !== 'object') return section;
  const { translations, ...base } = section.settings;
  const own = lang ? versions[String(lang).slice(0, 2)] : null;
  return { ...section, settings: own ? { ...base, ...own } : base };
};

const resolveSection = (section, sources) => {
  if (!sources || !section.bindings) return section;
  const settings = { ...(section.settings || {}) };
  Object.entries(section.bindings).forEach(([key, binding]) => {
    const value = typeof binding?.source === 'string' ? resolveSource(sources, binding.source) : undefined;
    if (value !== undefined && (typeof value === 'string' || typeof value === 'number')) settings[key] = String(value);
  });
  return { ...section, settings };
};

class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`Failed to render section ${this.props.sectionKey}`, error);
    }
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const SectionRenderer = ({ layout, registry, context }) => {
  const sections = layout?.template?.sections;
  if (!Array.isArray(sections)) return null;

  return sections.map((section) => {
    if (!section?.enabled) return null;
    const resolvedSection = resolveSection(localizeSection(section, context?.i18n?.language), context?.dynamicSources);
    const Component = registry[resolvedSection.type]?.component;
    if (!Component) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`Unknown storefront section type: ${resolvedSection.type}`);
      }
      return null;
    }

    const renderedSection = (
      <SectionErrorBoundary key={resolvedSection.key} sectionKey={resolvedSection.key}>
        <Component section={resolvedSection} context={context} />
      </SectionErrorBoundary>
    );

    if (!context?.isEditorPreview) return renderedSection;

    return (
      <div
        key={resolvedSection.key}
        className="editor-preview-section"
        data-editor-preview-section={resolvedSection.key}
        onClickCapture={(event) => {
          event.preventDefault();
          event.stopPropagation();
          window.parent?.postMessage({
            type: 'STOREFRONT_SECTION_SELECTED',
            sectionKey: resolvedSection.key,
          }, window.location.origin);
        }}
      >
        {renderedSection}
      </div>
    );
  });
};

export default SectionRenderer;
